import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Link2 } from "lucide-react";
import { PROJECTS, PROJECT_ROUTES } from "../data/projects";
import { TechTag, Disclaimer, usePageMeta, copyToClipboard, slug } from "./common";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import CiCaseStudy from "./CiCaseStudy";
import QmsDeepCase from "./QmsDeepCase";
import { SYNTHETIC_NOTE } from "../config";

const SECTION_ORDER = [
  ["businessProblem", "Business Problem"],
  ["whyItMattered", "Why It Mattered"],
  ["dataEvidence", "Data & Evidence"],
  ["methodology", "Methodology"],
  ["analysis", "Analysis"],
  ["solution", "Solution"],
  ["technology", "Technology"],
  ["qualityCompliance", "Quality / Compliance"],
  ["businessImpact", "Business Impact"],
  ["lessons", "Lessons / Transferability"],
];

const TECH_LABELS = {
  architecture: "Architecture",
  dataModel: "Data Model",
  logic: "Logic",
  validation: "Validation",
  failureHandling: "Failure Handling",
  scalability: "Scalability",
  qualityControls: "Quality Controls",
  lessonsLearned: "Lessons Learned",
};

export default function CaseStudyPage() {
  const { slug: param } = useParams();
  const entry = PROJECTS.map((p) => ({ p, route: PROJECT_ROUTES[p.id] })).find((e) => e.route && e.route.slug === `/projects/${param}`);
  const project = entry?.p;
  const route = entry?.route;

  usePageMeta({
    title: route ? route.seoTitle : "Case Study | Poojan Patel",
    description: route ? route.description : "Manufacturing case study portfolio.",
    canonicalPath: route ? route.slug : "/",
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [param]);

  if (!project) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center" data-testid="case-not-found">
        <p className="overline-label">404</p>
        <h1 className="font-sans tracking-tight text-3xl text-paper mt-4">Case study not found</h1>
        <Link to="/" className="inline-block mt-6 text-cyan font-mono text-sm hover:text-paper" data-testid="case-not-found-back">
          ← Back to the portfolio
        </Link>
      </div>
    );
  }

  const s = project.sections;
  return (
    <article className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid={`case-page-${project.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/" className="flex items-center gap-1.5 font-mono text-xs text-cyan hover:text-paper transition-colors" data-testid="case-back-link">
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" /> Back to portfolio
        </Link>
        <button
          data-testid="case-copy-link"
          onClick={() => copyToClipboard(window.location.href, "Case-study link")}
          className="flex items-center gap-1.5 font-mono text-xs px-3 py-1.5 border border-teal/40 rounded-sm text-pale/80 hover:border-cyan/60 hover:text-paper transition-colors"
        >
          <Link2 className="w-3.5 h-3.5" aria-hidden="true" /> Copy link
        </button>
      </div>

      <header className="mt-8">
        <p className="overline-label">{project.categories.join(" • ")}</p>
        <h1 className="font-sans tracking-tight text-3xl sm:text-4xl text-paper mt-3" data-testid="case-page-title">
          {project.title}
        </h1>
        {project.subtitle && <p className="text-cyan text-base mt-2 font-body">{project.subtitle}</p>}
        <p className="text-steel text-base mt-4 leading-relaxed max-w-3xl">{s.businessProblem}</p>
        <div className="flex flex-wrap gap-1.5 mt-5">
          {project.tags.map((t) => (
            <TechTag key={t}>{t}</TechTag>
          ))}
        </div>
      </header>

      {project.component === "ci" ? (
        <CiCaseStudy />
      ) : (
        <div className="mt-8">
          {SECTION_ORDER.map(([key, label], i) => (
            <div key={key} className="grid sm:grid-cols-[160px_1fr] gap-2 sm:gap-6 py-4 border-b border-cyan/10" data-testid={`case-section-${slug(label)}`}>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-cyan/70">
                {String(i + 1).padStart(2, "0")} — {label}
              </p>
              <p className="text-sm text-pale/85 leading-relaxed">{s[key]}</p>
            </div>
          ))}
          <div className="flex flex-wrap gap-1.5 mt-6">
            {project.tech.map((t) => (
              <TechTag key={t}>{t}</TechTag>
            ))}
          </div>
          {project.techDetails && (
            <Accordion type="single" collapsible className="mt-6" data-testid="case-tech-details">
              {Object.entries(project.techDetails).map(([key, text]) => (
                <AccordionItem key={key} value={key} className="border-cyan/15">
                  <AccordionTrigger
                    className="font-mono text-xs uppercase tracking-wider text-cyan hover:text-paper py-3"
                    data-testid={`case-tech-${slug(TECH_LABELS[key] || key)}`}
                  >
                    {TECH_LABELS[key] || key}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-pale/80 leading-relaxed">{text}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      )}

      {project.component === "qms-deep" && <QmsDeepCase />}

      <div className="mt-8">
        <Disclaimer>{SYNTHETIC_NOTE}</Disclaimer>
      </div>
    </article>
  );
}
